import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import { FaDownload, FaPlus, FaPrint, FaTrash, FaUsers, FaWallet, FaChartPie, FaShareAlt } from 'react-icons/fa';
import SEO from '../components/SEO';
import { formatPdfCurrency } from '../utils/calculations';
import { applyMobileInputHints } from '../utils/formEnhancements';

const CURRENCIES = [
  { code: 'INR', symbol: '₹' },
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'AED', symbol: 'AED' },
];

const makeMember = (name = '', contributed = 0) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name,
  contributed,
});

const makeExpense = (title = '', amount = 0, payer = '') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title,
  amount,
  payer,
});

const currencyFormatter = (currency, value) => {
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const options = {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  try {
    return new Intl.NumberFormat(locale, options).format(Number(value || 0));
  } catch (error) {
    return `${currency} ${Number(value || 0).toFixed(2)}`;
  }
};

const formatMoney = (currency, value) => currencyFormatter(currency, value);

const GroupCollectionReportGenerator = () => {
  const [eventName, setEventName] = useState('Durga Puja / Get Together / Birthday Party');
  const [currency, setCurrency] = useState('INR');
  const [notes, setNotes] = useState('Everyone contributes before the event. Any extra money can be kept for the next common expense or returned to the contributors.');
  const [members, setMembers] = useState([
    makeMember('Amit', 2000),
    makeMember('Priya', 1800),
    makeMember('Rahul', 2200),
    makeMember('Neha', 2000),
  ]);
  const [expenses, setExpenses] = useState([
    makeExpense('Pandals & décor', 4200, 'Amit'),
    makeExpense('Food & snacks', 3100, 'Priya'),
    makeExpense('Music & lights', 2400, 'Rahul'),
  ]);
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 768);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    applyMobileInputHints(pageRef.current);
  }, [members.length, expenses.length]);

  const addMember = () => setMembers((prev) => [...prev, makeMember('', 0)]);
  const removeMember = (id) => setMembers((prev) => prev.filter((member) => member.id !== id));
  const updateMember = (id, field, value) => {
    setMembers((prev) => prev.map((member) => (member.id === id ? { ...member, [field]: value } : member)));
  };

  const addExpense = () => setExpenses((prev) => [...prev, makeExpense('', 0, '')]);
  const removeExpense = (id) => setExpenses((prev) => prev.filter((expense) => expense.id !== id));
  const updateExpense = (id, field, value) => {
    setExpenses((prev) => prev.map((expense) => (expense.id === id ? { ...expense, [field]: value } : expense)));
  };

  const totals = useMemo(() => {
    const collected = members.reduce((sum, member) => sum + (Number(member.contributed) || 0), 0);
    const spent = expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);
    const activeMembers = members.filter((member) => member.name && member.name.trim()).length;
    const sharePerPerson = activeMembers > 0 ? spent / activeMembers : 0;

    const balances = members.map((member) => {
      const contributed = Number(member.contributed) || 0;
      const net = contributed - sharePerPerson;
      return {
        ...member,
        sharePerPerson,
        net,
        status: net >= 0 ? 'Should receive' : 'Should pay',
        amountDue: Math.abs(net),
      };
    });

    return {
      collected,
      spent,
      balance: collected - spent,
      activeMembers,
      sharePerPerson,
      balances,
    };
  }, [members, expenses]);

  const resetSampleData = () => {
    setEventName('Durga Puja / Get Together / Birthday Party');
    setCurrency('INR');
    setNotes('Everyone contributes before the event. Any extra money can be kept for the next common expense or returned to the contributors.');
    setMembers([
      makeMember('Amit', 2000),
      makeMember('Priya', 1800),
      makeMember('Rahul', 2200),
      makeMember('Neha', 2000),
    ]);
    setExpenses([
      makeExpense('Pandals & décor', 4200, 'Amit'),
      makeExpense('Food & snacks', 3100, 'Priya'),
      makeExpense('Music & lights', 2400, 'Rahul'),
    ]);
  };

  const printReport = () => {
    window.print();
  };

  const downloadPdf = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;

    doc.setFillColor(99, 102, 241);
    doc.rect(0, 0, pageWidth, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text('Group Collection Report', margin, 18);

    doc.setTextColor(17, 24, 39);
    doc.setFontSize(11);
    doc.text(`Event: ${eventName || 'Shared Event'}`, margin, 38);
    doc.text(`Currency: ${currency}`, margin, 44);
    doc.text(`Members: ${totals.activeMembers}`, margin, 50);

    let y = 64;
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text('Summary', margin, y);
    y += 10;
    doc.setFontSize(10);
    doc.text(`Total collected: ${formatPdfCurrency(currency, totals.collected)}`, margin, y);
    y += 7;
    doc.text(`Total spent: ${formatPdfCurrency(currency, totals.spent)}`, margin, y);
    y += 7;
    doc.text(`Balance remaining: ${formatPdfCurrency(currency, totals.balance)}`, margin, y);
    y += 7;
    doc.text(`Equal share per person: ${formatPdfCurrency(currency, totals.sharePerPerson)}`, margin, y);
    y += 16;

    doc.setFontSize(12);
    doc.text('Contributions', margin, y);
    y += 10;

    doc.setFontSize(9);
    members.forEach((member) => {
      if (y > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }
      const line = `${member.name || 'Unnamed'}: ${formatPdfCurrency(currency, Number(member.contributed) || 0)}`;
      doc.text(line, margin, y);
      y += 7;
    });

    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }

    y += 8;
    doc.setFontSize(12);
    doc.text('Expense List', margin, y);
    y += 10;
    doc.setFontSize(9);

    expenses.forEach((expense) => {
      if (y > pageHeight - 25) {
        doc.addPage();
        y = 20;
      }
      const line = `${expense.title || 'Expense'}: ${formatPdfCurrency(currency, Number(expense.amount) || 0)}${expense.payer ? ` | Payer: ${expense.payer}` : ''}`;
      doc.text(line, margin, y);
      y += 7;
    });

    y += 10;
    doc.setFontSize(10);
    doc.text('Distribution note:', margin, y);
    y += 7;
    const notesLines = doc.splitTextToSize(notes || 'Shared fund report.', pageWidth - (margin * 2));
    doc.text(notesLines, margin, y);

    const fileName = `${(eventName || 'group-collection').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-report.pdf`;
    doc.save(fileName);
  };

  return (
    <>
      <SEO
        title="Event Fund Report Generator"
        description="Create a shared money collection report for group events, Durga Puja, parties, and get-togethers. Track contributions, expenses, and fair distribution in one document."
        keywords="event fund report, puja fund report, party expense splitter, event money tracker, group collection tracker, get together contribution report"
        canonicalPath="/group-collection-report"
      />

      <div className="page-shell" style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 48px' }} ref={pageRef}>
        <div className="page-header-card" style={{ background: 'linear-gradient(135deg, #eef2ff, #f8fafc)', border: '1px solid #dfe3f5', borderRadius: 24, padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 18 }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, color: '#4338ca', fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                <FaUsers /> Group report
              </div>
              <h1 style={{ margin: '10px 0 8px', fontSize: 'clamp(2rem, 3vw, 2.8rem)' }}>Event Fund Report</h1>
              <p style={{ margin: 0, color: '#475569', maxWidth: 760, lineHeight: 1.6 }}>
                Track who contributed, what was spent, and how the leftover or dues should be shared for Durga Puja, parties, outings, or any group event.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', width: isCompactViewport ? '100%' : 'auto' }}>
              <button type="button" onClick={downloadPdf} style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer', width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaDownload style={{ marginRight: 8 }} /> Download PDF
              </button>
              <button type="button" onClick={printReport} style={{ background: '#0f172a', color: 'white', border: 'none', borderRadius: 12, padding: '12px 16px', fontWeight: 700, cursor: 'pointer', width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaPrint style={{ marginRight: 8 }} /> Print
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1.2fr 0.8fr', gap: 24 }}>
          <div style={{ display: 'grid', gap: 20 }}>
            <section style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20, boxShadow: '0 12px 30px rgba(15, 23, 42, 0.04)' }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Event details</h3>
              <div style={{ display: 'grid', gap: 16 }}>
                <label style={{ display: 'grid', gap: 8, fontSize: 14, color: '#334155' }}>
                  Event / purpose
                  <input value={eventName} onChange={(e) => setEventName(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 15 }} />
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: 16 }}>
                  <label style={{ display: 'grid', gap: 8, fontSize: 14, color: '#334155' }}>
                    Currency
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 15 }}>
                      {CURRENCIES.map((item) => (
                        <option key={item.code} value={item.code}>{item.code}</option>
                      ))}
                    </select>
                  </label>

                  <div style={{ display: 'grid', gap: 8, fontSize: 14, color: '#334155' }}>
                    <span>Quick action</span>
                    <button type="button" onClick={resetSampleData} style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}>
                      Reset sample data
                    </button>
                  </div>
                </div>

                <label style={{ display: 'grid', gap: 8, fontSize: 14, color: '#334155' }}>
                  Notes for the group
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 15, resize: 'vertical' }} />
                </label>
              </div>
            </section>

            <section style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20, boxShadow: '0 12px 30px rgba(15, 23, 42, 0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Contributors</h3>
                <button type="button" onClick={addMember} style={{ background: '#ecfdf5', color: '#166534', border: '1px solid #a7f3d0', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontWeight: 700 }}>
                  <FaPlus style={{ marginRight: 6 }} /> Add member
                </button>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                {members.map((member, index) => (
                  <div key={member.id} style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1.1fr 1fr auto', gap: 10, alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
                    <input
                      placeholder={`Member ${index + 1}`}
                      value={member.name}
                      onChange={(e) => updateMember(member.id, 'name', e.target.value)}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px' }}
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Contribution"
                      value={member.contributed}
                      onChange={(e) => updateMember(member.id, 'contributed', e.target.value)}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px' }}
                    />
                    <button type="button" onClick={() => removeMember(member.id)} style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, width: isCompactViewport ? '100%' : 40, height: 40, cursor: 'pointer' }}>
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20, boxShadow: '0 12px 30px rgba(15, 23, 42, 0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Expense list</h3>
                <button type="button" onClick={addExpense} style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontWeight: 700 }}>
                  <FaPlus style={{ marginRight: 6 }} /> Add expense
                </button>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                {expenses.map((expense, index) => (
                  <div key={expense.id} style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1.2fr 0.9fr 0.9fr auto', gap: 10, alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
                    <input
                      placeholder={`Expense ${index + 1}`}
                      value={expense.title}
                      onChange={(e) => updateExpense(expense.id, 'title', e.target.value)}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px' }}
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Amount"
                      value={expense.amount}
                      onChange={(e) => updateExpense(expense.id, 'amount', e.target.value)}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px' }}
                    />
                    <input
                      placeholder="Payer"
                      value={expense.payer}
                      onChange={(e) => updateExpense(expense.id, 'payer', e.target.value)}
                      style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px' }}
                    />
                    <button type="button" onClick={() => removeExpense(expense.id)} style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, width: isCompactViewport ? '100%' : 40, height: 40, cursor: 'pointer' }}>
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside style={{ display: 'grid', gap: 20 }}>
            <div style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: 'white', borderRadius: 20, padding: 20, boxShadow: '0 18px 40px rgba(79, 70, 229, 0.25)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, opacity: 0.9 }}>
                <FaWallet /> Summary
              </div>
              <div style={{ marginTop: 18, display: 'grid', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Total collected</div>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>{formatMoney(currency, totals.collected)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Total spent</div>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>{formatMoney(currency, totals.spent)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Balance</div>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>{formatMoney(currency, totals.balance)}</div>
                </div>
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
                <FaChartPie /> Distribution
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12 }}>
                  <div style={{ color: '#64748b', fontSize: 12 }}>Equal share per person</div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{formatMoney(currency, totals.sharePerPerson)}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12 }}>
                  <div style={{ color: '#64748b', fontSize: 12 }}>Members active</div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{totals.activeMembers}</div>
                </div>
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
                <FaShareAlt /> Who pays / gets back
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                {totals.balances.map((member) => (
                  <div key={member.id} style={{ background: '#f8fafc', borderRadius: 12, padding: 12, border: `1px solid ${member.net >= 0 ? '#bbf7d0' : '#fecaca'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                      <strong>{member.name || 'Unnamed member'}</strong>
                      <span style={{ fontSize: 12, fontWeight: 700, color: member.net >= 0 ? '#166534' : '#b91c1c' }}>{member.net >= 0 ? 'Should receive' : 'Should pay'}</span>
                    </div>
                    <div style={{ marginTop: 8, color: '#475569', fontSize: 13 }}>{formatMoney(currency, Number(member.contributed) || 0)} contributed</div>
                    <div style={{ marginTop: 4, color: '#475569', fontSize: 13 }}>
                      {member.net >= 0 ? `Receives ${formatMoney(currency, member.amountDue)}` : `Pays ${formatMoney(currency, member.amountDue)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default GroupCollectionReportGenerator;
